import React, { useRef, useCallback, useState } from 'react'
import { Box, Typography, Modal, IconButton } from '@mui/material'
import { useTranslation } from '../../../../../shared/hooks/useIntractionTranslate'
import { XMarkIcon, ArrowPathIcon, EyeIcon, PencilSquareIcon } from '@heroicons/react/24/outline'
import { TrainingSprintConfig } from '../../types'
import { getDisplayDurationMinutes } from '../../utils/activityConfiguration'
import { INTERAX_COLORS } from '../../../../../constants/colors'
import { Button } from '../../../../../interaxDesignSystemComponents'
import { EmailEditor } from '../EmailEditor'
import type { SprintEmailContent } from '../../../../../../../pure-js/types/Sprint'
import { buildCalendarUrlsForActivity } from './buildCalendarUrls'
import DOMPurify from 'dompurify'
import { escapeHtml } from '../../../../../../../pure-js/libs/Common'

const PREVIEW_VARIABLES: Record<string, string> = {
  name: 'Anna',
  teamName: 'Alpha Team',
  facilitatorName: 'Erik Johansson',
  appURL: 'https://app.intraction.com'
}

function resolvePreviewHtml(body: string, activities: TrainingSprintConfig['activities']): string {
  let html = body
  const previewVars = { ...PREVIEW_VARIABLES }

  // Replace variable chips (span tags) with resolved values (plain text, no chip styling)
  html = html.replace(
    /<span[^>]*data-variable-id="([^"]*)"[^>]*>.*?<\/span>/gs,
    (_match, varId) => previewVars[varId] ?? `{{${varId}}}`
  )

  // Replace raw {{variable}} placeholders
  for (const [key, value] of Object.entries(previewVars)) {
    html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value)
  }

  // Replace {{ACTIVITIES_BLOCK}} with rendered activities
  const activitiesHtml = renderActivitiesHtml(activities)
  html = html.replace(/\{\{ACTIVITIES_BLOCK\}\}/g, activitiesHtml)
  // Also handle the div-based block from deserialize
  html = html.replace(/<div[^>]*data-activities-block="true"[^>]*>[^<]*<\/div>/g, activitiesHtml)

  // The body comes from the stored sprint, which another editor of the sprint wrote; nothing in it may run here.
  return DOMPurify.sanitize(html)
}

function renderActivitiesHtml(activities: TrainingSprintConfig['activities']): string {
  if (!activities || activities.length === 0) {
    return '<p style="color: #94a3b8; font-style: italic;">No activities scheduled.</p>'
  }

  const items = activities
    .map((a) => {
      const date = a.scheduledDate
        ? new Date(a.scheduledDate).toLocaleDateString('en-US', {
            weekday: 'long',
            month: 'long',
            day: 'numeric',
            year: 'numeric'
          })
        : ''
      const time = a.scheduledTime || ''
      const duration = getDisplayDurationMinutes(a)
      const { googleCalendarUrl, outlookCalendarUrl } = buildCalendarUrlsForActivity({
        title: a.title,
        description: a.description,
        scheduledDate: a.scheduledDate,
        scheduledTime: a.scheduledTime,
        duration
      })
      return `<div style="border-left: 3px solid #f59e0b; padding-left: 12px; margin-bottom: 8px;">
        <strong>${escapeHtml(a.title)}</strong><br/>
        <span style="color: #94a3b8; font-size: 13px;">${date}${time ? ` at ${time}` : ''} &middot; ${duration} min</span><br/>
        <span style="font-size: 12px;">
          <a href="${escapeHtml(googleCalendarUrl)}">Google Cal</a> &middot;
          <a href="${escapeHtml(outlookCalendarUrl)}">Outlook</a>
        </span>
      </div>`
    })
    .join('')

  return `<div style="margin: 8px 0;">${items}</div>`
}

export interface PreviewEmailModalProps {
  isOpen: boolean
  onClose: () => void
  sprintData: TrainingSprintConfig
  onSave: (emailContent: SprintEmailContent) => void
}

export const PreviewEmailModal: React.FC<PreviewEmailModalProps> = ({ isOpen, onClose, sprintData, onSave }) => {
  const { t } = useTranslation()
  const editorStateRef = useRef<{ resetToDefaults: () => void } | null>(null)
  const [currentContent, setCurrentContent] = useState<SprintEmailContent | null>(null)
  const [isPreviewMode, setIsPreviewMode] = useState(false)

  const handleContentChange = useCallback((content: SprintEmailContent) => {
    setCurrentContent(content)
  }, [])

  const handleDone = useCallback(() => {
    if (currentContent) {
      onSave(currentContent)
    }
    setIsPreviewMode(false)
    onClose()
  }, [currentContent, onSave, onClose])

  const handleReset = useCallback(() => {
    editorStateRef.current?.resetToDefaults()
  }, [])

  if (!isOpen) return null

  return (
    <Modal open={isOpen} onClose={handleDone} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <Box
        sx={{
          bgcolor: INTERAX_COLORS.offBlack,
          borderRadius: 2,
          boxShadow: 24,
          maxWidth: '64rem',
          width: '100%',
          mx: 2,
          maxHeight: '90vh',
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'column'
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderBottom: 1,
            borderColor: INTERAX_COLORS.thunderGrey
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="h3" className="text-headline-3" sx={{ color: INTERAX_COLORS.white }}>
              {t('facilitator.trainingSprint.emailEditor.title')}
            </Typography>
            <IconButton
              onClick={() => setIsPreviewMode(!isPreviewMode)}
              sx={{
                border: 1,
                borderColor: INTERAX_COLORS.heritageBlue,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                color: INTERAX_COLORS.typographyText,
                fontSize: '13px',
                gap: 0.5,
                '&:hover': { bgcolor: INTERAX_COLORS.heritageBlue40 }
              }}
            >
              {isPreviewMode ? <PencilSquareIcon className="w-4 h-4" /> : <EyeIcon className="w-4 h-4" />}
              <span style={{ fontSize: '13px' }}>
                {isPreviewMode
                  ? t('facilitator.trainingSprint.emailEditor.editMode')
                  : t('facilitator.trainingSprint.emailEditor.previewMode')}
              </span>
            </IconButton>
          </Box>
          <IconButton
            onClick={handleDone}
            sx={{
              border: 1,
              borderColor: INTERAX_COLORS.sun20,
              '&:hover': { bgcolor: INTERAX_COLORS.heritageBlue40 }
            }}
          >
            <XMarkIcon className="w-5 h-5" style={{ color: INTERAX_COLORS.sun }} />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ p: 3, overflowY: 'auto', flex: 1 }}>
          {isPreviewMode && (
            <Box
              sx={{
                bgcolor: '#ffffff',
                borderRadius: 1,
                p: 3,
                color: '#1a1a1a',
                fontSize: '14px',
                lineHeight: 1.6,
                '& p': { margin: '0 0 8px 0' },
                '& strong': { fontWeight: 600 },
                '& a': { color: '#3b82f6' }
              }}
              dangerouslySetInnerHTML={{
                __html: resolvePreviewHtml(currentContent?.body || '', sprintData.activities || [])
              }}
            />
          )}
          {/* Keep the editor mounted while previewing — unmounting it discards unsaved edits */}
          <Box sx={{ display: isPreviewMode ? 'none' : 'block' }}>
            <EmailEditor
              initialContent={sprintData.emailContent}
              onContentChange={handleContentChange}
              editorStateRef={editorStateRef}
            />
          </Box>
        </Box>

        {/* Footer */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            p: 3,
            borderTop: 1,
            borderColor: INTERAX_COLORS.thunderGrey
          }}
        >
          <Button
            variant="secondary"
            onClick={handleReset}
            icon={<ArrowPathIcon className="w-4 h-4" />}
            iconPosition="left"
          >
            {t('facilitator.trainingSprint.emailEditor.resetToDefault')}
          </Button>
          <Button variant="primary" onClick={handleDone}>
            {t('facilitator.trainingSprint.emailEditor.done')}
          </Button>
        </Box>
      </Box>
    </Modal>
  )
}
